// hooks/usePhotoTransformer.js - Hook for managing photo transformations
import { useCallback } from 'react';
import { usePhotoEditorContext } from '../context/PhotoEditorContext';
import { cropCanvas, rotateCanvas, flipCanvas } from '../utils/photoEditor';

const usePhotoTransformer = () => {
  const {
    previewCanvasRef,
    originalSizeCanvasRef,
    transformations,
    setTransformations
  } = usePhotoEditorContext();

  // Crop image to specified aspect ratio
  const cropImage = useCallback((widthRatio, heightRatio) => {
    const previewCanvas = previewCanvasRef.current;
    const originalSizeCanvas = originalSizeCanvasRef.current;
    
    if (!previewCanvas || !originalSizeCanvas) return;
    
    // Update transformations state
    setTransformations(prev => ({
      ...prev,
      cropRatio: { width: widthRatio, height: heightRatio }
    }));
    
    // Apply crop to both canvases
    cropCanvas(previewCanvas, widthRatio, heightRatio);
    cropCanvas(originalSizeCanvas, widthRatio, heightRatio);
  }, [previewCanvasRef, originalSizeCanvasRef, setTransformations]);

  // Rotate image by specified angle
  const rotateImage = useCallback((angle) => {
    const previewCanvas = previewCanvasRef.current;
    const originalSizeCanvas = originalSizeCanvasRef.current;
    
    if (!previewCanvas || !originalSizeCanvas) return;
    
    // Update transformations state
    setTransformations(prev => ({
      ...prev,
      rotation: (prev.rotation + angle) % 360
    }));
    
    // Apply rotation to both canvases
    rotateCanvas(previewCanvas, angle);
    rotateCanvas(originalSizeCanvas, angle);
  }, [previewCanvasRef, originalSizeCanvasRef, setTransformations]);

  // Flip image horizontally or vertically
  const flipImage = useCallback((direction) => {
    const previewCanvas = previewCanvasRef.current;
    const originalSizeCanvas = originalSizeCanvasRef.current;
    
    if (!previewCanvas || !originalSizeCanvas) return;
    
    // Update transformations state
    setTransformations(prev => ({
      ...prev,
      flipped: {
        ...prev.flipped,
        [direction]: !prev.flipped[direction]
      }
    }));
    
    // Apply flip to both canvases
    flipCanvas(previewCanvas, direction);
    flipCanvas(originalSizeCanvas, direction);
  }, [previewCanvasRef, originalSizeCanvasRef, setTransformations]);

  return {
    cropImage,
    rotateImage,
    flipImage,
    transformations
  };
};

export default usePhotoTransformer;
